/**
 * Water Quality Calculation Utilities
 * 
 * This module contains all water quality calculation functions used across the aquaculture management system.
 * These functions are extracted from the Water screen for better modularity, testability, and reusability.
 */

import type { TranslationKey } from '../i18n/en'
import { isValidTableDate, normalizeToIsoZMidnight } from './dateNormalization'
import type { ProductionSpeciesKey } from '../data/productionSpeciesPresets'

/**
 * Generate current date in MM/DD/YYYY format
 * Ultra-light date function for consistent date formatting across the application
 * 
 * @returns {string} Current date in MM/DD/YYYY format
 */
export const getTodayDate = (): string => {
  const today = new Date()
  return `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`
}

/**
 * Estimate dissolved oxygen concentration based on temperature and altitude
 * 
 * Uses the standard DO solubility formula with altitude correction:
 * - Base formula: DO = 14.652 - 0.41022*T + 0.007991*T² - 0.000077774*T³ (sea level)
 * - Altitude correction: P = P₀ * (1 - 0.0065*h/288.15)^5.257 (barometric formula)
 * - DO correction: DO_corrected = DO_sea_level * (P/P₀)
 * 
 * @param {number} temperature - Water temperature in Celsius
 * @param {number} altitudeMeters - Farm altitude in meters above sea level (default: 0)
 * @returns {number} Estimated dissolved oxygen in mg/L, rounded to 2 decimal places
 */
export const estimateDissolvedOxygen = (temperature: number, altitudeMeters: number = 0): number => {
  // Input validation and clamping for safety
  if (temperature < -5 || temperature > 45) {
    console.warn(`Temperature ${temperature}°C is outside normal range (-5°C to 45°C). Clamping to safe bounds.`)
    const clampedTemp = Math.max(-5, Math.min(45, temperature))
    return parseFloat((14.652 - 0.41022 * clampedTemp + 0.007991 * clampedTemp * clampedTemp - 0.000077774 * clampedTemp * clampedTemp * clampedTemp).toFixed(2))
  }
  
  // Base DO at sea level using standard polynomial approximation
  const doSeaLevel = 14.652 - 0.41022 * temperature + 0.007991 * temperature * temperature - 0.000077774 * temperature * temperature * temperature
  
  // Handle negative altitude (below sea level)
  if (altitudeMeters < 0) {
    console.warn(`Negative altitude ${altitudeMeters}m detected. Using sea level calculation.`)
    return parseFloat(doSeaLevel.toFixed(2))
  }
  
  // Altitude correction using barometric formula
  // P/P₀ = (1 - 0.0065*h/288.15)^5.257
  const pressureRatio = Math.pow(1 - (0.0065 * altitudeMeters) / 288.15, 5.257)
  
  // Apply altitude correction
  const doCorrected = doSeaLevel * pressureRatio
  
  // Sanity check for reasonable DO values
  if (doCorrected < 0 || doCorrected > 20) {
    console.warn(`Calculated DO ${doCorrected} mg/L seems unrealistic. Check inputs: T=${temperature}°C, Alt=${altitudeMeters}m`)
  }
  
  return parseFloat(doCorrected.toFixed(2))
}

/**
 * Calculate dissolved oxygen saturation percentage
 * 
 * Calculates the percentage saturation relative to sea level conditions (standard reference).
 * Formula: Saturation % = (Actual DO / Maximum DO at current conditions) × 100
 * 
 * @param {number} actualDO - Measured dissolved oxygen in mg/L
 * @param {number} temperature - Water temperature in Celsius  
 * @param {number} altitudeMeters - Farm altitude in meters above sea level (default: 0)
 * @returns {number} DO saturation percentage, rounded to 1 decimal place
 */
export const calculateDOSaturation = (actualDO: number, temperature: number, altitudeMeters: number = 0): number => {
  // Input validation
  if (actualDO < 0) {
    console.warn(`Negative actual DO ${actualDO} mg/L detected. Using 0.`)
    actualDO = 0
  }
  
  if (actualDO > 50) {
    console.warn(`Unusually high actual DO ${actualDO} mg/L detected. Check measurement.`)
  }
  
  // Calculate saturation relative to sea level conditions (standard reference)
  const seaLevelMaxDO = estimateDissolvedOxygen(temperature, 0)
  
  if (seaLevelMaxDO === 0) {
    console.warn('Sea level max DO is 0. Cannot calculate saturation.')
    return 0
  }
  
  const saturation = (actualDO / seaLevelMaxDO) * 100
  
  // Sanity check for reasonable saturation values
  if (saturation > 200) {
    console.warn(`Calculated saturation ${saturation}% seems very high. Check inputs: DO=${actualDO} mg/L, T=${temperature}°C`)
  }
  
  return parseFloat(saturation.toFixed(1))
}

/**

/**
 * Validate date format (ISO 8601: YYYY-MM-DD)
 * Accepts both MM/DD/YYYY and YYYY-MM-DD for safe transition
 * @param {string} dateStr - Date string to validate
 * @returns {boolean} True if date format is valid or empty, false otherwise
 */
export const isValidDateFormat = (dateStr: string): boolean => {
  if (!dateStr || dateStr.trim() === '') return true // Empty is valid
  // Accept any parseable date that we can normalize into an ISO Z timestamp
  // (ISO date-only, ISO datetimes, and legacy slash formats).
  return isValidTableDate(dateStr)
}

/**
 * Parse ISO 8601 (YYYY-MM-DD) or MM/DD/YYYY date string to Date object
 * @param {string} dateStr - Date string in ISO 8601 or MM/DD/YYYY format
 * @returns {Date} Parsed date object, or epoch date for invalid input
 */
export const parseDate = (dateStr: string): Date => {
  if (!dateStr || dateStr.trim() === '') return new Date(0)

  const normalized = normalizeToIsoZMidnight(dateStr)
  if (normalized.ok) return new Date(normalized.isoZ)

  const parsed = new Date(dateStr)
  return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed
}



/**
 * Get temperature status for aquaculture (optimized for salmon/trout)
 * 
 * @param {number | null} temp - Temperature in Celsius
 * @returns {object} Status object with color and text
 */
export const getTemperatureStatus = (
  temp: number | null,
  speciesKey?: ProductionSpeciesKey | null
): { color: string; labelKey: TranslationKey } => {
  if (temp === null || temp === undefined) return { color: '#666', labelKey: 'water.tempStatus.noData' }

  const isWarmWater = speciesKey === 'shrimp' || speciesKey === 'tilapia'
  const minTemp = isWarmWater ? 26 : 9
  const maxTemp = isWarmWater ? 35 : 17

  if (temp < minTemp) return { color: '#dc3545', labelKey: 'water.tempStatus.tooCold' }
  if (temp > maxTemp) return { color: '#dc3545', labelKey: 'water.tempStatus.tooWarm' }
  return { color: '#28a745', labelKey: isWarmWater ? 'water.tempStatus.optimalRange' : 'water.tempStatus.optimalForSfr' }
}

/**
 * Calculate pressure ratio for altitude correction
 * 
 * @param {number} altitudeMeters - Altitude in meters above sea level
 * @returns {number} Pressure ratio (P/P₀)
 */
export const calculatePressureRatio = (altitudeMeters: number): number => {
  return Math.pow(1 - (0.0065 * altitudeMeters) / 288.15, 5.257)
}